import { ExperienceResourceArray } from '../../../Types';
import { LearningDesign } from '../../../Types/SectionTemplates';
import { deriveSectionCompetencies, deriveSectionOfferingTypes } from './Utils';

/**
 * Determines if a curated section should be shown based on filter selections.
 *
 * This follows the program catalog pattern:
 * - selectedCompetencies/selectedProductTypes contain explicitly selected values
 * - Empty array means nothing selected (show no results)
 * - A section passes if it has ANY matching competency AND ANY matching product type
 */
export const shouldShowCuratedSection = (
  section: LearningDesign,
  selectedCompetencies: string[],
  selectedProductTypes: string[],
  experiencesArray: ExperienceResourceArray,
): boolean => {
  // If no competencies selected, nothing can match
  if (selectedCompetencies.length === 0) {
    return false;
  }

  // If no product types selected, nothing can match
  if (selectedProductTypes.length === 0) {
    return false;
  }

  const sectionCompetencies = deriveSectionCompetencies(section, experiencesArray);
  const sectionOfferingTypes = deriveSectionOfferingTypes(section, experiencesArray);

  // Check if section has ANY matching competency
  const passesCompetencyFilter = sectionCompetencies.some((comp) =>
    selectedCompetencies.includes(comp),
  );

  // Check if section has ANY matching product type
  const passesProductTypeFilter = sectionOfferingTypes.some((type) =>
    selectedProductTypes.includes(type),
  );

  return passesCompetencyFilter && passesProductTypeFilter;
};

/**
 * Filter an array of curated sections based on current filter selections.
 */
export const filterCuratedSections = (
  sections: LearningDesign[],
  selectedCompetencies: string[],
  selectedProductTypes: string[],
  experiencesArray: ExperienceResourceArray,
): LearningDesign[] => {
  return sections.filter((section) =>
    shouldShowCuratedSection(section, selectedCompetencies, selectedProductTypes, experiencesArray),
  );
};
